import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Box,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Edit as EditIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  Description as DocumentIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import empresasApi from '../api/empresasApi';

function MiEmpresa() {
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchEmpresa();
  }, []);

  const fetchEmpresa = async () => {
    try {
      setLoading(true);
      const response = await empresasApi.obtenerMiEmpresa();
      setEmpresa(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al obtener los datos de la empresa:', error);
      setError('No se pudieron cargar los datos de la empresa. Por favor, inténtelo de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/home/miempresa/editar`);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        p: 3
      }}>
        <CircularProgress size={60} sx={{ mb: 2, color: '#1A237E' }} />
        <Typography variant="h6" color="text.secondary">
          Cargando información de la empresa...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontSize: '1rem'
            }
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      {/* Header responsive */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1"
            sx={{ 
              fontWeight: 'bold',
              color: '#1A237E',
              mb: 0.5
            }}
          >
            Mi Empresa
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Información y configuración de tu empresa
          </Typography>
        </Box>
        
        <Button 
          variant="contained" 
          startIcon={<EditIcon />} 
          onClick={handleEdit}
          sx={{
            background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 'bold',
            boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
              boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          Editar información
        </Button>
      </Box>

      {empresa && (
        <Box sx={{ width: '100%' }}>
          {/* Card principal */}
          <Paper 
            elevation={0}
            sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid rgba(26, 35, 126, 0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
            }}
          >
            {/* Header de la empresa */}
            <Box sx={{
              background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
              color: 'white',
              p: { xs: 3, sm: 4 },
              display: 'flex',
              alignItems: 'center',
              gap: 3
            }}>
              <Avatar
                sx={{
                  width: { xs: 60, sm: 80 },
                  height: { xs: 60, sm: 80 },
                  background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 100%)',
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  fontWeight: 'bold'
                }}
              >
                {empresa.nombre_empresa?.charAt(0)?.toUpperCase() || 'E'}
              </Avatar>
              
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant={isMobile ? "h5" : "h4"} 
                  component="h2" 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: 1,
                    wordBreak: 'break-word'
                  }}
                >
                  {empresa.nombre_empresa}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label="Empresa Activa" 
                    size="small"
                    sx={{ 
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      color: '#4CAF50',
                      fontWeight: 'bold'
                    }}
                  />
                  <Chip 
                    label={empresa.det_pais} 
                    size="small"
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white'
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Contenido de la tarjeta */}
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Grid container spacing={3}>
                {/* Información General */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#1A237E',
                        fontWeight: 'bold',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <BusinessIcon sx={{ color: '#4FC3F7' }} />
                      Información General
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <DocumentIcon sx={{ color: '#4FC3F7', fontSize: '1.2rem' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Documento
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {empresa.nro_doc_empresa}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PersonIcon sx={{ color: '#4FC3F7', fontSize: '1.2rem' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Contacto
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {empresa.contacto_empresa}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LanguageIcon sx={{ color: '#4FC3F7', fontSize: '1.2rem' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            País
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {empresa.det_pais}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <MoneyIcon sx={{ color: '#4FC3F7', fontSize: '1.2rem' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Moneda
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {empresa.simbolo_moneda}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                
                {/* Información de Contacto */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#1A237E',
                        fontWeight: 'bold',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <EmailIcon sx={{ color: '#4FC3F7' }} />
                      Información de Contacto
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <EmailIcon sx={{ color: '#4FC3F7', fontSize: '1.2rem' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Email
                          </Typography>
                          <Typography 
                            variant="body1" 
                            fontWeight="500"
                            sx={{ wordBreak: 'break-word' }}
                          >
                            {empresa.email_empresa}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PhoneIcon sx={{ color: '#4FC3F7', fontSize: '1.2rem' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Teléfono
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {empresa.telefono_empresa}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Footer */}
              <Box sx={{ 
                mt: 4, 
                pt: 3, 
                borderTop: '1px solid rgba(26, 35, 126, 0.1)',
                textAlign: 'center'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Última actualización: {new Date(empresa.fecha_sistema).toLocaleDateString('es-PE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
            </CardContent>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

export default MiEmpresa;